import { getCachedMember } from '@/lib/db/auth-session';
import { getSupabase } from '@/lib/db/client';

export type StorageBucket = 'profile-photos' | 'post-photos' | 'ace-photos' | 'game-covers';

const SIGNED_URL_TTL_SEC = 60 * 60 * 24 * 365;
const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'm4v']);
const VIDEO_MIME: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  m4v: 'video/x-m4v',
};

export function parseStoragePath(url: string): { bucket: StorageBucket; path: string } | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)/);
    if (!m) return null;
    return { bucket: m[1] as StorageBucket, path: decodeURIComponent(m[2]) };
  } catch {
    return null;
  }
}

export async function getSignedUrl(bucket: StorageBucket, path: string): Promise<string> {
  const { data, error } = await getSupabase().storage.from(bucket).createSignedUrl(path, SIGNED_URL_TTL_SEC);
  if (error) throw error;
  return data.signedUrl;
}

export async function refreshStorageUrl(url: string): Promise<string> {
  const parsed = parseStoragePath(url);
  if (!parsed) return url;
  return getSignedUrl(parsed.bucket, parsed.path);
}

function isVideoUpload(file: Blob, filename?: string): boolean {
  if (file.type.startsWith('video/')) return true;
  const ext = filename?.split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.has(ext);
}

function contentTypeForUpload(file: Blob, filename?: string): string {
  if (file.type) return file.type;
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (ext && VIDEO_MIME[ext]) return VIDEO_MIME[ext];
  return 'image/jpeg';
}

function uploadKindLabel(isVideo: boolean): string {
  return isVideo ? 'Video' : 'Photo';
}

export async function uploadFile(bucket: StorageBucket, file: Blob, filename?: string): Promise<string> {
  const me = await getCachedMember();
  if (!me) throw new Error('Sign in again to upload photos or video.');

  const isVideo = isVideoUpload(file, filename);
  const kind = uploadKindLabel(isVideo);
  const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file.size > maxBytes) {
    const limitMb = Math.round(maxBytes / (1024 * 1024));
    throw new Error(`${kind} is too large (max ${limitMb} MB). Try a shorter clip or lower resolution.`);
  }

  const sb = getSupabase();
  const ext = filename?.split('.').pop() || (isVideo ? 'mp4' : 'jpg');
  const key = `${me.id}/${Date.now()}.${ext}`;
  const { error } = await sb.storage.from(bucket).upload(key, file, {
    upsert: true,
    contentType: contentTypeForUpload(file, filename),
  });
  if (error) {
    const msg = error.message || `${kind} upload failed`;
    if (/bucket not found/i.test(msg)) {
      throw new Error(
        `${kind} storage is not set up yet. Post text only, or ask an admin to create the storage bucket.`,
      );
    }
    if (/row-level security|violates.*policy/i.test(msg)) {
      throw new Error(
        `${kind} upload was blocked for this account. Try signing out and back in. If it persists, ask an admin to verify your member profile is linked to your login.`,
      );
    }
    if (/payload too large|entity too large/i.test(msg)) {
      throw new Error(`${kind} exceeds the server size limit. Try a smaller file.`);
    }
    if (/mime type|not allowed|invalid.*content.?type|unsupported.*type/i.test(msg)) {
      throw new Error(
        `${kind} type was rejected by storage. Ask an admin to allow video/mp4, video/quicktime, and video/webm on post-photos (size limit ≥ 100 MB).`,
      );
    }
    throw new Error(msg);
  }
  return getSignedUrl(bucket, key);
}

/** Remove an uploaded blob when a post create fails after upload. */
export async function deleteUploadedFile(url: string): Promise<void> {
  const parsed = parseStoragePath(url);
  if (!parsed) return;
  await getSupabase().storage.from(parsed.bucket).remove([parsed.path]);
}

export function fileToBlob(file: File): Blob {
  return file;
}

export async function cropImageToSquare(file: File, size = 800): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const side = Math.min(img.width, img.height);
    const sx = (img.width - side) / 2;
    const sy = (img.height - side) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Crop failed'))), 'image/jpeg', 0.9);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
